import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { appendScopeToApiUrl } from '@/lib/queryClient';
import enTranslations from './en.json';
import esTranslations from './es.json';
import ptTranslations from './pt.json';

export type SupportedLanguages = 'en' | 'es' | 'br';

interface Translations {
  [key: string]: string | Translations;
}

interface LanguageContextType {
  language: SupportedLanguages;
  setLanguage: (lang: SupportedLanguages) => void;
  /**
   * Basic translation lookup with optional interpolation.
   * Usage: t('approvalDefinitions.declareDefinitionsApproved', { year: 2025 })
   */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /**
   * Rich translation returning ReactNode allowing simple inline strong/em tags via pseudo-markup.
   * Example source string: "I declare the plan {year} is <strong>Approved</strong>."
   * Usage: tr('approvalDefinitions.declareDefinitionsApproved', { year: 2025 })
   */
  tr: (key: string, vars?: Record<string, string | number>) => React.ReactNode;
  ready: boolean;
}

function normalizeLanguage(value?: string | null): SupportedLanguages | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase().replace(/_/g, '-');
  const base = normalized.split('-')[0];
  if (base === 'pt') return 'br';
  if (base === 'en' || base === 'es' || base === 'br') return base as SupportedLanguages;
  return null;
}

const baseTranslations: Record<SupportedLanguages, Translations> = {
  en: enTranslations,
  es: esTranslations,
  br: ptTranslations,
};

const dynamicOverrides: Record<SupportedLanguages, Translations> = { en: {}, es: {}, br: {} };

interface OverridesApiResponse {
  language: SupportedLanguages;
  count: number;
  overrides: Record<string, string>;
  fetchedAt: string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const getBrowserLanguage = (): SupportedLanguages => {
  return normalizeLanguage(navigator.language) || 'en';
};

const getValueFromLanguage = (language: SupportedLanguages, key: string): string | undefined => {
  const keys = key.split('.');
  let translation: Translations | string | undefined = {
    ...baseTranslations[language],
    ...dynamicOverrides[language],
  } as Translations;

  for (const keyPart of keys) {
    if (typeof translation === 'object' && translation[keyPart]) {
      translation = translation[keyPart];
    } else {
      translation = undefined;
      break;
    }
  }
  return typeof translation === 'string' ? translation : undefined;
};

const getTranslationValue = (language: SupportedLanguages, key: string): string => {
  let translation = getValueFromLanguage(language, key);
  if (!translation && language !== 'en') {
    translation = getValueFromLanguage('en', key);
  }
  return translation || key;
};

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

function parseRichText(str: string): React.ReactNode {
  const tokens: React.ReactNode[] = [];
  const regex = /(<\/?strong>|<\/?em>|<br\s*\/?>)/i;
  let remaining = str;
  while (remaining.length) {
    const idx = remaining.search(regex);
    if (idx === -1) {
      tokens.push(remaining);
      break;
    }
    if (idx > 0) tokens.push(remaining.slice(0, idx));
    const match = remaining.match(regex);
    if (!match) break;
    const tag = match[0].toLowerCase();
    remaining = remaining.slice(idx + tag.length);
    switch (tag) {
      case '<strong>': {
        const endIdx = remaining.toLowerCase().indexOf('</strong>');
        if (endIdx === -1) {
          tokens.push(React.createElement('strong', { key: tokens.length }, remaining));
          remaining = '';
          break;
        }
        const content = remaining.slice(0, endIdx);
        tokens.push(React.createElement('strong', { key: tokens.length }, parseRichText(content)));
        remaining = remaining.slice(endIdx + '</strong>'.length);
        break;
      }
      case '<em>': {
        const endIdx = remaining.toLowerCase().indexOf('</em>');
        if (endIdx === -1) {
          tokens.push(React.createElement('em', { key: tokens.length }, remaining));
          remaining = '';
          break;
        }
        const content = remaining.slice(0, endIdx);
        tokens.push(React.createElement('em', { key: tokens.length }, parseRichText(content)));
        remaining = remaining.slice(endIdx + '</em>'.length);
        break;
      }
      case '<br/>':
      case '<br>':
        tokens.push(React.createElement('br', { key: tokens.length }));
        break;
      case '</strong>':
      case '</em>':
        break;
      default:
        tokens.push(tag);
    }
  }
  return tokens.length === 1 ? tokens[0] : tokens;
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initial = normalizeLanguage(localStorage.getItem('appLanguage')) || getBrowserLanguage();
  const [language, setLanguage] = useState<SupportedLanguages>(initial);
  const [ready, setReady] = useState(false);
  const inFlight = useRef<AbortController | null>(null);
  console.log('[i18n] Initial language:', initial);

  useEffect(() => {
    localStorage.setItem('appLanguage', language);
  }, [language]);

  const loadOverrides = useCallback(
    async (_lang: SupportedLanguages) => {
      if (inFlight.current) inFlight.current.abort();
      const ac = new AbortController();
      inFlight.current = ac;
      try {
        setReady(false);
        const resp = await fetch(appendScopeToApiUrl('/api/i18n/overrides'), {
          signal: ac.signal,
          headers: { Accept: 'application/json' },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data: OverridesApiResponse = await resp.json();
        const normalized = normalizeLanguage(data.language);
        if (normalized && normalized !== _lang) {
          setLanguage(normalized);
          return;
        }
        if (normalized) {
          dynamicOverrides[normalized] = data.overrides as Translations;
        }
      } catch (e) {
        console.warn('[i18n] Error loading overrides', e);
      } finally {
        setReady(true);
      }
    },
    [setLanguage],
  );

  useEffect(() => {
    loadOverrides(language);
  }, [language, loadOverrides]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const base = getTranslationValue(language, key);
      return interpolate(base, vars);
    },
    [language],
  );

  const tr = useCallback(
    (key: string, vars?: Record<string, string | number>): React.ReactNode => {
      const raw = t(key, vars);
      return parseRichText(raw);
    },
    [t],
  );

  const contextValue = { language, setLanguage, t, tr, ready };
  return React.createElement(LanguageContext.Provider, { value: contextValue }, children);
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

export default { LanguageProvider, useTranslation };
